import React, { useMemo } from 'react'
import { styled } from '@mui/system'
import { IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { connect, useDispatch } from 'react-redux'
import { AllState } from '../../../store'
import { closeHelpDrawer } from '../../../store/helpDrawer'
import Drawer from '../../components/drawers/Drawer'
import { SupportSiteLink } from './SupportSiteLink'
import { HelpChatContent } from '../../components/HelpChatbot/HelpChatContent'
import { isAiAvailable } from '../../../lib/functions/ai'
import { colorPalette } from '../../style/colorPalette'
import { FONT_FAMILY } from '../../../styles/commonStyles'

const Container = styled('div')(({ theme }) => ({
  flexGrow: 1,
  background: theme.palette.background.paper,
  overflow: 'hidden',
  width: '100%',
  height: 'calc(100% - 56px)',
  fontFamily: FONT_FAMILY,
  display: 'flex',
  flexDirection: 'column',
}))

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  background: colorPalette.monotone[0],
})

const HeaderTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '16px',
  color: colorPalette.monotone[10],
})

const ChatContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  padding: '16px',
  overflow: 'hidden',
  gap: '16px',
})

const DisabledMessage = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexGrow: 1,
  padding: '32px',
  textAlign: 'center',
  color: colorPalette.monotone[5],
  fontSize: '14px',
})

const DRAWER_DEFAULT_WIDTH = 400
const DRAWER_MIN_WIDTH = 320

interface StateProps {
  open: boolean
}

const mapStateToProps = (state: AllState): StateProps => ({
  open: state.helpDrawer.open,
})

const HelpDrawerComponent: React.FC<StateProps> = ({ open }) => {
  const dispatch = useDispatch()

  const drawerWidthConfig = useMemo(() => {
    const maxWidth = window.innerWidth - 50
    return {
      default: DRAWER_DEFAULT_WIDTH,
      min: DRAWER_MIN_WIDTH,
      max: maxWidth,
    }
  }, [])

  const handleClose = () => {
    dispatch(closeHelpDrawer())
  }

  const aiAvailable = isAiAvailable()

  return (
    <Drawer
      elevation={true}
      open={open}
      onClose={handleClose}
      defaultWidth={drawerWidthConfig.default}
      minWidth={drawerWidthConfig.min}
      maxWidth={drawerWidthConfig.max}
      anchor="right"
      isResizable={true}
      closeOnClickAway={true}
    >
      <Container id="help-drawer-container">
        <Header>
          <HeaderTitle>ヘルプ</HeaderTitle>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Header>
        <SupportSiteLink />
        <ChatContainer>
          {aiAvailable ? (
            <HelpChatContent />
          ) : (
            <DisabledMessage>
              AI機能が無効化されています。
              <br />
              有効化するには管理者に問い合わせてください。
            </DisabledMessage>
          )}
        </ChatContainer>
      </Container>
    </Drawer>
  )
}

export const HelpDrawer = connect(mapStateToProps)(HelpDrawerComponent)

export default HelpDrawer
